import type { IconName } from '@/shared/icons';

import {
  DashboardData,
  DashboardMetric,
  PerformanceData,
  PerformanceResponse,
  RecentActivity,
  AIUsageData,
  Appointment,
  DashboardFilters,
} from '../types';
import { CHART_COLORS } from '@/shared/constants/charts';

/**
 * Mapeamento de IDs de métricas para ícones
 */
const metricIcons: Record<string, IconName> = {
  // Support metrics
  'chats-attended': 'message-square',
  'avg-response-time': 'clock',
  'avg-service-time': 'clock',
  'sla-rate': 'check-circle',
  'satisfaction': 'check-circle',
  'abandonment-rate': 'alert-circle',
  'queue-chats': 'clock',
  'ongoing-chats': 'activity',
  'avg-messages-per-chat': 'message-square',
};

/**
 * Gera dados de sparkline baseado na tendência da métrica
 * Simula uma série de 14 pontos com tendência consistente
 */
function generateSparklineData(trend: 'up' | 'down' | 'neutral', seed: number): number[] {
  const points = 14;
  const data: number[] = [];
  let base = 50 + (seed % 30);

  for (let i = 0; i < points; i++) {
    // Variação aleatória determinística baseada no seed
    const variation = Math.sin(seed * 0.7 + i * 1.3) * 8 + Math.cos(seed * 0.3 + i * 0.9) * 5;

    if (trend === 'up') {
      base += (i / points) * 3;
    } else if (trend === 'down') {
      base -= (i / points) * 3;
    }

    data.push(Math.max(5, Math.round(base + variation)));
  }
  return data;
}

/**
 * Extrai percentual numérico do texto de mudança
 * Ex: "+12% em relação ao mês passado" → 12
 */
function extractChangePercent(change: string): number | undefined {
  const match = change.match(/([+-]?\d+(?:\.\d+)?)\s*%/);
  if (match) return parseFloat(match[1]);
  return undefined;
}

/**
 * Adiciona ícones, sparklineData e changePercent às métricas do backend.
 *
 * Todas as métricas recebem sparkline — inclusive o card de 2 valores
 * (`secondaryValue`, ex: "Atendimentos" = criados + finalizados), que segue
 * o mesmo padrão visual dos demais 8 cards da dash.
 * Exportada para teste.
 */
export function addIconsToMetrics(metrics: Omit<DashboardMetric, 'icon'>[]): DashboardMetric[] {
  return metrics.map((metric, index) => ({
    ...metric,
    icon: metricIcons[metric.id] || 'activity',
    sparklineData: generateSparklineData(metric.trend, index * 7 + metric.id.length),
    changePercent: extractChangePercent(metric.change),
  }));
}

/**
 * Mock data - será substituído por API calls reais
 */

// Mock metrics data
const mockMetrics: DashboardMetric[] = [
  {
    id: 'total-chats',
    title: 'Chats Atendidos',
    value: '2.847',
    change: '+12% em relação ao mês passado',
    trend: 'up',
    icon: 'message-square',
  },
  {
    id: 'ongoing-chats',
    title: 'Chats em Andamento',
    value: '23',
    change: '+5% em relação a ontem',
    trend: 'up',
    icon: 'activity',
  },
  {
    id: 'queue-chats',
    title: 'Chats na Fila',
    value: '7',
    change: '-15% em relação a ontem',
    trend: 'down',
    icon: 'clock',
  },
  {
    id: 'active-agents',
    title: 'Agentes Ativos',
    value: '12',
    change: 'Mesmo que ontem',
    trend: 'neutral',
    icon: 'user-check',
  },
  {
    id: 'conversion-rate',
    title: 'Taxa de Conversão',
    value: '18.5%',
    change: '+2.3% em relação ao mês passado',
    trend: 'up',
    icon: 'trending-up',
  },
  {
    id: 'appointments',
    title: 'Agendamentos',
    value: '145',
    change: '+8% em relação ao mês passado',
    trend: 'up',
    icon: 'calendar',
  },
  {
    id: 'calls',
    title: 'Chamadas',
    value: '89',
    change: '-3% em relação ao mês passado',
    trend: 'down',
    icon: 'phone',
  },
  {
    id: 'orders',
    title: 'Pedidos',
    value: '234',
    change: '+15% em relação ao mês passado',
    trend: 'up',
    icon: 'shopping-bag',
  },
  {
    id: 'response-time',
    title: 'Tempo de Resposta',
    value: '2.3min',
    change: '-8% em relação ao mês passado',
    trend: 'up',
    icon: 'clock',
  },
  {
    id: 'satisfaction',
    title: 'Satisfação',
    value: '4.8/5',
    change: '+0.2 em relação ao mês passado',
    trend: 'up',
    icon: 'check-circle',
  },
  {
    id: 'abandoned-chats',
    title: 'Chats Abandonados',
    value: '12',
    change: '-25% em relação ao mês passado',
    trend: 'up',
    icon: 'alert-circle',
  },
  {
    id: 'new-contacts',
    title: 'Novos Contatos',
    value: '467',
    change: '+22% em relação ao mês passado',
    trend: 'up',
    icon: 'users',
  },
];

// Mock performance data
const mockPerformanceData: PerformanceData[] = [
  { date: '2024-01-15', atendimentos: 120, leads: 45, reunioes: 8, pedidos: 23 },
  { date: '2024-01-16', atendimentos: 135, leads: 52, reunioes: 12, pedidos: 18 },
  { date: '2024-01-17', atendimentos: 148, leads: 38, reunioes: 15, pedidos: 31 },
  { date: '2024-01-18', atendimentos: 162, leads: 61, reunioes: 9, pedidos: 27 },
  { date: '2024-01-19', atendimentos: 141, leads: 47, reunioes: 11, pedidos: 22 },
  { date: '2024-01-20', atendimentos: 158, leads: 55, reunioes: 14, pedidos: 29 },
  { date: '2024-01-21', atendimentos: 172, leads: 49, reunioes: 16, pedidos: 35 },
];

// Mock recent activities
const mockRecentActivities: RecentActivity[] = [
  {
    id: 1,
    type: 'message',
    title: 'Novo chat iniciado',
    description: 'Cliente interessado em consultoria',
    user: { name: 'Ana Silva' },
    timestamp: new Date(Date.now() - 1000 * 60 * 5), // 5 minutes ago
  },
  {
    id: 2,
    type: 'sale',
    title: 'Venda concluída',
    description: 'Pacote Premium vendido',
    user: { name: 'Carlos Santos' },
    timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
  },
  {
    id: 3,
    type: 'lead',
    title: 'Novo lead qualificado',
    description: 'Lead convertido para oportunidade',
    user: { name: 'Maria Costa' },
    timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
  },
  {
    id: 4,
    type: 'meeting',
    title: 'Reunião agendada',
    description: 'Demo do produto marcada',
    user: { name: 'João Oliveira' },
    timestamp: new Date(Date.now() - 1000 * 60 * 45), // 45 minutes ago
  },
  {
    id: 5,
    type: 'call',
    title: 'Chamada atendida',
    description: 'Suporte técnico resolvido',
    user: { name: 'Paula Lima' },
    timestamp: new Date(Date.now() - 1000 * 60 * 60), // 1 hour ago
  },
];

// Mock AI usage data - paleta monocromática roxa
const mockAIUsageData: AIUsageData[] = [
  { name: 'GPT-4', value: 45, color: CHART_COLORS[0] },
  { name: 'Claude', value: 30, color: CHART_COLORS[1] },
  { name: 'Gemini', value: 15, color: CHART_COLORS[2] },
  { name: 'Outros', value: 10, color: CHART_COLORS[3] },
];

// Mock appointments data
const mockAppointments: Appointment[] = [
  {
    id: 1,
    title: 'Reunião de vendas',
    client: { name: 'João Silva' },
    datetime: new Date(Date.now() + 1000 * 60 * 30), // 30 minutes from now
    type: 'meeting',
    location: 'Sala de reuniões A',
    status: 'confirmed',
  },
  {
    id: 2,
    title: 'Demo do produto',
    client: { name: 'Maria Santos' },
    datetime: new Date(Date.now() + 1000 * 60 * 60 * 2), // 2 hours from now
    type: 'video',
    status: 'scheduled',
  },
  {
    id: 3,
    title: 'Follow-up comercial',
    client: { name: 'Pedro Costa' },
    datetime: new Date(Date.now() + 1000 * 60 * 60 * 4), // 4 hours from now
    type: 'call',
    status: 'pending',
  },
  {
    id: 4,
    title: 'Apresentação proposta',
    client: { name: 'Ana Oliveira' },
    datetime: new Date(Date.now() + 1000 * 60 * 60 * 24), // tomorrow
    type: 'meeting',
    location: 'Escritório do cliente',
    status: 'confirmed',
  },
];

import { api } from '@/lib/api';
/**
 * Service para dados do dashboard - conectado ao backend real
 */
export const dashboardService = {
  /**
   * Busca dados completos do dashboard
   */
  async getDashboardData(_filters?: DashboardFilters): Promise<DashboardData> {
    // TODO: Implementar quando houver endpoint consolidado no backend
    return {
      metrics: mockMetrics,
      performance: mockPerformanceData,
      recentActivities: mockRecentActivities,
      aiUsage: mockAIUsageData,
      upcomingAppointments: mockAppointments,
    };
  },

  /**
   * Busca métricas por período (área única: Atendimento).
   * `range` acompanha `period === 'custom'` (F81 — PeriodPicker shared).
   * `channelId` filtra todas as métricas por canal de atendimento.
   */
  async getMetrics(
    period: string,
    range?: { start: string; end: string },
    channelId?: string
  ): Promise<DashboardMetric[]> {
    const response = await api.get('/dashboard/metrics', {
      params: {
        // Única área existente — o schema do backend exige o param.
        area: 'support',
        period,
        // Mesmo fuso do gráfico: sem isso o card recorta a janela em
        // America/Sao_Paulo e "criados" deixa de bater com a série.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(period === 'custom' && range
          ? { startDate: range.start, endDate: range.end }
          : {}),
        ...(channelId ? { channelId } : {}),
      }
    });
    const metricsFromBackend = response.data.data.metrics;
    return addIconsToMetrics(metricsFromBackend);
  },

  /**
   * Busca dados de performance para gráficos.
   * `range` acompanha `period === 'custom'` (F81 — PeriodPicker shared).
   * `channelId` filtra as séries (atendimentos e leads) pelo canal.
   */
  async getPerformanceData(
    period: string,
    timezone?: string,
    range?: { start: string; end: string },
    channelId?: string
  ): Promise<PerformanceResponse> {
    const response = await api.get('/dashboard/performance', {
      params: {
        period,
        timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(period === 'custom' && range
          ? { startDate: range.start, endDate: range.end }
          : {}),
        ...(channelId ? { channelId } : {}),
      }
    });
    return response.data.data;
  },

  /**
   * Busca atividades recentes
   */
  async getRecentActivities(limit = 5): Promise<RecentActivity[]> {
    const response = await api.get('/dashboard/activities', {
      params: { limit }
    });
    return response.data.data;
  },

  /**
   * Busca dados de consumo de IA
   */
  async getAIUsageData(period: string): Promise<AIUsageData[]> {
    const response = await api.get('/dashboard/ai-usage', {
      params: { period }
    });
    return response.data.data;
  },

  /**
   * Busca próximos compromissos
   */
  async getUpcomingAppointments(limit = 4): Promise<Appointment[]> {
    const response = await api.get('/dashboard/appointments', {
      params: { limit }
    });
    return response.data.data;
  },

  /**
   * Atualiza uma métrica específica
   */
  async updateMetric(metricId: string, value: string | number): Promise<DashboardMetric> {
    // TODO: Implementar quando houver endpoint de update no backend
    const metric = mockMetrics.find(m => m.id === metricId);
    if (!metric) {
      throw new Error(`Metric with id ${metricId} not found`);
    }

    return { ...metric, value };
  },
};